/* eslint-disable no-param-reassign */

import React from 'react';
import { connect } from 'react-redux';
import {
  Dialog,
  InputAdornment,
  LinearProgress,
  TextField,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import toast from '../../../../../utils/toast';
import { IAppState } from '../../../../../state/reducers';
import { ISecurityProfileOption } from '../../../../../state/securityProfiles';
import {
  IProps,
  IStateProps,
  IDispatchProps,
} from './interfaces';
import {
  enableClipperKeyboardControls,
  disableClipperKeyboardControls,
} from '../../../state/videoPlayer';
import {
  closeCreateDownloadDialog,
  updateCreatedAssetField,
  resetCreateDownloadDialog,
  selectSelectedSecurityProfileOption,
  getWatermarks,
  updateSelectedWatermark,
} from '../../../state/createDownloadDialog';
import { createAsset } from '../../../state/actions/createAsset';
import { resetSelectedClipsPreview } from '../../../../../state/selectedClipsPreview';
import { selectIsUserVaultSuperAdmin } from '../../../../../state/user/userOrgRole';
import {
  VaultButton,
  ClipsPreviewPlayer,
} from '../../../../Shared';
import { SelectedClipsTotalDuration } from '../../../../Shared/SelectedClipsPreview';
import {
  DialogHeader,
  DialogHeaderTitle,
  DialogContentContainer,
  ControlContainer,
  ControlLabel,
  DialogActionsOverride,
  ActionContainer,
  Selectable,
} from '../../../../Shared/styles/dialog';
import { ActionType } from '../../../../../api/vault/assetStatus/interfaces';
import { ExchangeClipsContainer, WatermarkOption, WatermarkThumbnail } from './styles';
import { IAsset, IDataHit } from '../../../../../api/vault/searchEngine';
import { ProgressContainer } from '../../../../Shared/JobList/styles';
import { selectSelectedClips } from '../../../state/clipList';
import { optionsToReactSelect, selectExchangeQualifiersOptions } from '../../../../../state/lookups';
import { buildExchangeTitle, orderClipsBasedOnQualifier } from '../../../utils/exchange';

class CreateDownloadDialog extends React.Component<IProps, { isSaving: boolean }> {
  constructor(props: IProps) {
    super(props);
    this.state = {
      isSaving: false,
    };
  }

  componentDidMount(): void {
    const { onGetWatermarks } = this.props;
    onGetWatermarks();
  }

  handleCancel = () => {
    const { closeDialog } = this.props;

    closeDialog();
  };

  handleSave = async () => {
    const {
      asset,
      createAssetConnect,
      saveMessage,
      closeDialog,
    } = this.props;

    this.setState({ isSaving: true });
    const action = saveMessage.toLowerCase() as ActionType;

    await createAssetConnect({
      newAsset: asset,
      metadata: [],
      actionType: action,
    });
    closeDialog();

    this.setState({ isSaving: false });
  };

  handleDialogExited = () => {
    const {
      enableClipperKeyboardControlsConnect,
      resetDialog,
      resetPreview,
    } = this.props;

    enableClipperKeyboardControlsConnect();
    resetDialog();
    resetPreview();
  };

  selectEnteredValue = (e: React.FocusEvent<HTMLInputElement>) => {
    e.target.select();
  };

  renderExchangeCreate = () => {
    const {
      exchangeSchedule, selectedClips, exchangeQualifierOptions, closeDialog,
    } = this.props;

    // Find duplicate clip names
    const clipNames = selectedClips.map((clip) => clip.name);
    const duplicateClipNames = clipNames.filter((name, index) => clipNames.indexOf(name) !== index);
    if (duplicateClipNames.length > 0) {
      toast.error(`Duplicate qualifiers names found: ${[...new Set(duplicateClipNames)].join(', ')}. Please select unique qualifiers before creating exchange assets.`, {
        position: toast.POSITION.TOP_RIGHT,
      });
      closeDialog();
    }
    // Sort and render clips
    const sortedClips = orderClipsBasedOnQualifier(selectedClips, exchangeQualifierOptions);
    return (
      <ExchangeClipsContainer>
        {sortedClips.map((clip) => (
          <span key={clip.id}>{buildExchangeTitle(clip, exchangeSchedule)}</span>
        ))}
      </ExchangeClipsContainer>
    );
  };

  renderStandardCreate = () => {
    const {
      asset,
      updateAssetField,
      securityProfileOptions,
      selectedSecurityProfileOption,
      isVaultSuperAdmin,
      watermarkOptions,
      selectedWatermark,
      onUpdateSelectedWatermark,
    } = this.props;

    return (
      <>
        <ClipsPreviewPlayer />
        <ControlContainer>
          <ControlLabel>Title *</ControlLabel>
          <TextField
            fullWidth
            margin="dense"
            variant="outlined"
            value={asset.title}
            onChange={(e) => updateAssetField('title', e.target.value)}
            onFocus={this.selectEnteredValue}
            data-auto="CreateTitleTxt"
          />
        </ControlContainer>
        <ControlContainer>
          <ControlLabel>Description</ControlLabel>
          <TextField
            fullWidth
            margin="dense"
            variant="outlined"
            value={asset.description}
            onChange={(e) => updateAssetField('description', e.target.value)}
            onFocus={this.selectEnteredValue}
            multiline
            rows={3}
            data-auto="CreateDescTxt"
          />
        </ControlContainer>
        {isVaultSuperAdmin && (
          <ControlContainer>
            <ControlLabel>Security Profile</ControlLabel>
            <Selectable
              menuPlacement="top"
              options={securityProfileOptions}
              value={selectedSecurityProfileOption}
              onChange={(option: unknown) => updateAssetField('securityProfileId', (option as ISecurityProfileOption)?._id)}
            />
          </ControlContainer>
        )}
        {false && ( // TODO: remove once logic is working. Hiding for deployment
          <ControlContainer>
            <ControlLabel>Watermark</ControlLabel>
            <Autocomplete
              renderOption={(option, props) => (
                <WatermarkOption>
                  <WatermarkThumbnail src={props._source.thumbnailImageUrl} />
                  <span>{props._source.title}</span>
                </WatermarkOption>
              )}
              getOptionLabel={(option) => option._source.title.toString()}
              options={watermarkOptions}
              onChange={(event: any, newWatermark: IDataHit<IAsset>) => { onUpdateSelectedWatermark(newWatermark); }}
              value={selectedWatermark}
              style={{ width: '100%' }}
              renderInput={(params) => {
                if (selectedWatermark !== null) {
                  params.InputProps.startAdornment = (
                    <InputAdornment position="start"><WatermarkThumbnail src={selectedWatermark._source.thumbnailImageUrl} /></InputAdornment>
                  );
                }
                return <TextField variant="outlined" {...params} />;
              }}
            />
          </ControlContainer>
        )}
      </>
    );
  };

  render() {
    const {
      isOpen,
      saveMessage,
      asset,
      disableClipperKeyboardControlsConnect,
      isExchangeLiveEvent,
    } = this.props;

    const { isSaving } = this.state;

    return (
      <Dialog
        id="VirtualClipper.CreateDownloadDialog"
        open={isOpen}
        fullWidth
        maxWidth="md"
        TransitionProps={{
          onEntered: disableClipperKeyboardControlsConnect,
          onExited: this.handleDialogExited,
        }}
      >
        {isOpen && (
          <>
            <DialogHeader>
              <DialogHeaderTitle data-auto="CreateAssetModal">
                {isExchangeLiveEvent ? 'Creating and Pushing Exchange Assets' : `${saveMessage} Asset`}
                {!isExchangeLiveEvent && <SelectedClipsTotalDuration />}
              </DialogHeaderTitle>
            </DialogHeader>
            <ProgressContainer>
              {isSaving && <LinearProgress />}
            </ProgressContainer>
            <DialogContentContainer>
              {isExchangeLiveEvent ? this.renderExchangeCreate() : this.renderStandardCreate()}
            </DialogContentContainer>
            <DialogActionsOverride disableSpacing>
              <ActionContainer>
                <VaultButton
                  variant="plainOnPaper"
                  onClick={this.handleCancel}
                  autoFocus
                  data-auto="CreateCancelBtn"
                  disabled={isSaving}
                >
                  Cancel
                </VaultButton>
                <VaultButton
                  variant="primaryOnWhite"
                  onClick={this.handleSave}
                  disabled={!asset.title || isSaving}
                  data-auto="CreateAssetBtn"
                >
                  {saveMessage}
                </VaultButton>
              </ActionContainer>
            </DialogActionsOverride>
          </>
        )}
      </Dialog>
    );
  }
}

const mapStateToProps = (state: IAppState): IStateProps => ({
  saveMessage: state.createDownloadDialog?.saveMessage ?? '',
  isOpen: state.createDownloadDialog?.isOpen ?? false,
  asset: state.createDownloadDialog?.asset,
  securityProfileOptions: state.securityProfiles?.securityProfileOptions ?? [],
  selectedSecurityProfileOption: selectSelectedSecurityProfileOption(state),
  isVaultSuperAdmin: selectIsUserVaultSuperAdmin(state),
  watermarkOptions: state.createDownloadDialog?.watermarkOptions ?? [],
  selectedWatermark: state.createDownloadDialog?.selectedWatermark,
  isExchangeLiveEvent: state.clipperMediaContent?.isExchangeLiveEvent ?? false,
  exchangeSchedule: state.clipperMediaContent?.exchangeSchedule,
  selectedClips: selectSelectedClips(state),
  exchangeQualifierOptions: optionsToReactSelect(selectExchangeQualifiersOptions(state)),
});

const mapDispatchToProps: IDispatchProps = {
  closeDialog: closeCreateDownloadDialog,
  updateAssetField: updateCreatedAssetField,
  resetDialog: resetCreateDownloadDialog,
  resetPreview: resetSelectedClipsPreview,
  createAssetConnect: createAsset,
  enableClipperKeyboardControlsConnect: enableClipperKeyboardControls,
  disableClipperKeyboardControlsConnect: disableClipperKeyboardControls,
  onGetWatermarks: getWatermarks,
  onUpdateSelectedWatermark: updateSelectedWatermark,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(CreateDownloadDialog);
